<template>
  <div class="job-queue">
    <div class="job-queue__content">
      <div class="tabs-container">
        <!-- Tabs in style of bim-projects -->
        <q-tabs
          v-model="activeTab"
          dense
          class="text-grey"
          active-color="primary"
          indicator-color="primary"
          align="left"
        >
          <q-tab name="jobs" label="Задачи (job_tab)" />
          <q-tab name="groups" label="Группы задач (job_group_tab)" />
        </q-tabs>

        <q-separator />

        <q-tab-panels v-model="activeTab" animated class="tab-panels">
          <!-- Jobs tab -->
          <q-tab-panel name="jobs" class="tab-panel">
            <!-- Filters -->
            <div class="mb-4 filters-row">
              <div class="filter-item">
                <label class="block text-sm text-gray-600 mb-1">
                  Статус задач
                </label>
                <select
                  v-model="jobsStatus"
                  class="border rounded px-2 py-1 text-sm"
                  @change="onJobsFilterChange"
                >
                  <option :value="null">Все</option>
                  <option value="pending">pending</option>
                  <option value="running">running</option>
                  <option value="completed">completed</option>
                  <option value="failed">failed</option>
                </select>
              </div>

              <div class="filter-item">
                <label class="block text-sm text-gray-600 mb-1">
                  Дата c
                </label>
                <input
                  type="date"
                  v-model="jobsDateFrom"
                  class="border rounded px-2 py-1 text-sm"
                  @change="onJobsFilterChange"
                />
              </div>

              <div class="filter-item">
                <label class="block text-sm text-gray-600 mb-1">
                  Дата по
                </label>
                <input
                  type="date"
                  v-model="jobsDateTo"
                  class="border rounded px-2 py-1 text-sm"
                  @change="onJobsFilterChange"
                />
              </div>

              <div class="filter-item filter-item--wide">
                <label class="block text-sm text-gray-600 mb-1">
                  Поиск по функции
                </label>
                <input
                  v-model="jobsSearch"
                  class="border rounded px-2 py-1 text-sm"
                  placeholder="sql_template..."
                  @keyup.enter="onJobsFilterChange"
                  @blur="onJobsFilterChange"
                />
              </div>
            </div>

            <!-- Jobs table -->
            <div class="overflow-auto border rounded">
              <table class="min-w-full text-xs">
                <thead class="bg-gray-100">
                  <tr>
                    <th class="px-2 py-1 text-left">ID</th>
                    <th class="px-2 py-1 text-left">Статус</th>
                    <th class="px-2 py-1 text-left">Группа</th>
                    <th class="px-2 py-1 text-left">Даты / попытки</th>
                    <th class="px-2 py-1 text-left">Функция</th>
                    <th class="px-2 py-1 text-left">Параметры</th>
                    <th class="px-2 py-1 text-left">Ошибка</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="jobsLoading">
                    <td colspan="11" class="px-2 py-2 text-center text-gray-500">
                      Загрузка...
                    </td>
                  </tr>
                  <tr v-else-if="!jobs.length">
                    <td colspan="11" class="px-2 py-2 text-center text-gray-500">
                      Нет данных
                    </td>
                  </tr>
                  <tr v-for="row in jobs" :key="row.job_id" class="border-t">
                    <td class="px-2 py-1">{{ row.job_id }}</td>
                    <td
                      class="px-2 py-1 status-cell"
                      :class="statusClass(row.status)"
                    >
                      {{ row.status }}
                    </td>
                    <td class="px-2 py-1">
                      {{ row.job_group_id || "—" }}
                    </td>
                    <td class="px-2 py-1 whitespace-pre-wrap max-w-xs">
                      <div v-if="row.created_at" class="date-row">
                        <q-icon name="event_note" size="14px" class="mr-1" />
                        {{ formatDateTime(row.created_at) }}
                      </div>
                      <div v-if="row.started_at" class="date-row">
                        <q-icon name="play_arrow" size="14px" class="mr-1" />
                        {{ formatDateTime(row.started_at) }}
                      </div>
                      <div v-if="row.finished_at" class="date-row">
                        <q-icon
                          name="check_circle"
                          size="14px"
                          class="mr-1"
                        />
                        {{ formatDateTime(row.finished_at) }}
                      </div>
                      <div v-if="row.attempts != null" class="date-row">
                        <q-icon name="cached" size="14px" class="mr-1" />
                        {{ row.attempts }}
                      </div>
                    </td>
                    <td class="px-2 py-1 whitespace-pre-wrap max-w-xs">
                      {{ row.sql_template }}
                    </td>
                    <td class="px-2 py-1 whitespace-pre-wrap max-w-xs">
                      {{ row.params_json }}
                    </td>
                    <td class="px-2 py-1 whitespace-pre-wrap max-w-xs">
                      {{ row.error_message }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Jobs pagination -->
            <div class="mt-2 flex items-center justify-between text-sm">
              <div>
                Страница {{ jobsPage }} из {{ jobsTotalPages }} (всего
                {{ jobsTotal }})
              </div>
              <div class="space-x-2">
                <button
                  class="px-2 py-1 border rounded disabled:opacity-50"
                  :disabled="jobsPage <= 1 || jobsLoading"
                  @click="changeJobsPage(jobsPage - 1)"
                >
                  Назад
                </button>
                <button
                  class="px-2 py-1 border rounded disabled:opacity-50"
                  :disabled="jobsPage >= jobsTotalPages || jobsLoading"
                  @click="changeJobsPage(jobsPage + 1)"
                >
                  Вперёд
                </button>
              </div>
            </div>
          </q-tab-panel>

          <!-- Groups tab -->
          <q-tab-panel name="groups" class="tab-panel">
            <!-- Filters -->
            <div class="mb-4 filters-row">
              <div class="filter-item">
                <label class="block text-sm text-gray-600 mb-1">
                  Статус групп
                </label>
                <select
                  v-model="groupsStatus"
                  class="border rounded px-2 py-1 text-sm"
                  @change="onGroupsFilterChange"
                >
                  <option :value="null">Все</option>
                  <option value="pending">pending</option>
                  <option value="running">running</option>
                  <option value="completed">completed</option>
                  <option value="failed">failed</option>
                </select>
              </div>

              <div class="filter-item">
                <label class="block text-sm text-gray-600 mb-1">
                  Дата c
                </label>
                <input
                  type="date"
                  v-model="groupsDateFrom"
                  class="border rounded px-2 py-1 text-sm"
                  @change="onGroupsFilterChange"
                />
              </div>

              <div class="filter-item">
                <label class="block text-sm text-gray-600 mb-1">
                  Дата по
                </label>
                <input
                  type="date"
                  v-model="groupsDateTo"
                  class="border rounded px-2 py-1 text-sm"
                  @change="onGroupsFilterChange"
                />
              </div>

              <div class="filter-item filter-item--wide">
                <label class="block text-sm text-gray-600 mb-1">
                  Поиск по агрегатору
                </label>
                <input
                  v-model="groupsSearch"
                  class="border rounded px-2 py-1 text-sm"
                  placeholder="aggregator_sql_template..."
                  @keyup.enter="onGroupsFilterChange"
                  @blur="onGroupsFilterChange"
                />
              </div>
            </div>

            <!-- Groups table -->
            <div class="overflow-auto border rounded">
              <table class="min-w-full text-xs">
                <thead class="bg-gray-100">
                  <tr>
                    <th class="px-2 py-1 text-left">ID группы</th>
                    <th class="px-2 py-1 text-left">Статус</th>
                    <th class="px-2 py-1 text-left">Всего задач</th>
                    <th class="px-2 py-1 text-left">Осталось</th>
                    <th class="px-2 py-1 text-left">Создано</th>
                    <th class="px-2 py-1 text-left">Обновлено</th>
                    <th class="px-2 py-1 text-left">Aggregator SQL</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="groupsLoading">
                    <td colspan="7" class="px-2 py-2 text-center text-gray-500">
                      Загрузка...
                    </td>
                  </tr>
                  <tr v-else-if="!groups.length">
                    <td colspan="7" class="px-2 py-2 text-center text-gray-500">
                      Нет данных
                    </td>
                  </tr>
                  <tr
                    v-for="row in groups"
                    :key="row.job_group_id"
                    class="border-t"
                  >
                    <td class="px-2 py-1">{{ row.job_group_id }}</td>
                    <td
                      class="px-2 py-1 status-cell"
                      :class="statusClass(row.status)"
                    >
                      {{ row.status }}
                    </td>
                    <td class="px-2 py-1">{{ row.total_jobs }}</td>
                    <td class="px-2 py-1">{{ row.remaining_jobs }}</td>
                    <td class="px-2 py-1">
                      {{ formatDateTime(row.created_at) }}
                    </td>
                    <td class="px-2 py-1">
                      {{ formatDateTime(row.updated_at) }}
                    </td>
                    <td class="px-2 py-1 whitespace-pre-wrap max-w-xs">
                      {{ row.aggregator_sql_template }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Groups pagination -->
            <div class="mt-2 flex items-center justify-between text-sm">
              <div>
                Страница {{ groupsPage }} из {{ groupsTotalPages }} (всего
                {{ groupsTotal }})
              </div>
              <div class="space-x-2">
                <button
                  class="px-2 py-1 border rounded disabled:opacity-50"
                  :disabled="groupsPage <= 1 || groupsLoading"
                  @click="changeGroupsPage(groupsPage - 1)"
                >
                  Назад
                </button>
                <button
                  class="px-2 py-1 border rounded disabled:opacity-50"
                  :disabled="groupsPage >= groupsTotalPages || groupsLoading"
                  @click="changeGroupsPage(groupsPage + 1)"
                >
                  Вперёд
                </button>
              </div>
            </div>
          </q-tab-panel>
        </q-tab-panels>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import { metricsAPI } from "@/views/Main/services/api";

const activeTab = ref("jobs");

// Jobs state
const jobs = ref([]);
const jobsPage = ref(1);
const jobsPageSize = ref(20);
const jobsTotal = ref(0);
const jobsStatus = ref(null);
const jobsDateFrom = ref(null);
const jobsDateTo = ref(null);
const jobsSearch = ref("");
const jobsLoading = ref(false);

// Groups state
const groups = ref([]);
const groupsPage = ref(1);
const groupsPageSize = ref(20);
const groupsTotal = ref(0);
const groupsStatus = ref(null);
const groupsDateFrom = ref(null);
const groupsDateTo = ref(null);
const groupsSearch = ref("");
const groupsLoading = ref(false);

const jobsTotalPages = computed(() =>
  jobsTotal.value > 0 ? Math.ceil(jobsTotal.value / jobsPageSize.value) : 1
);
const groupsTotalPages = computed(() =>
  groupsTotal.value > 0 ? Math.ceil(groupsTotal.value / groupsPageSize.value) : 1
);

const formatDateTime = (value) => {
  if (!value) return "";
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;

    const pad = (n) => String(n).padStart(2, "0");
    const day = pad(d.getDate());
    const month = pad(d.getMonth() + 1);
    const year = d.getFullYear();
    const hours = pad(d.getHours());
    const minutes = pad(d.getMinutes());
    const seconds = pad(d.getSeconds());

    return `${day}.${month}.${year} ${hours}:${minutes}:${seconds}`;
  } catch {
    return value;
  }
};

const statusClass = (status) => {
  const s = (status || "").toLowerCase();
  if (s === "pending") return "status-pending";
  if (s === "running") return "status-running";
  if (s === "completed") return "status-completed";
  if (s === "failed") return "status-failed";
  return "";
};

const loadJobs = async () => {
  if (jobsLoading.value) return;
  jobsLoading.value = true;
  try {
    const res = await metricsAPI.getJobQueueJobs({
      page: jobsPage.value,
      page_size: jobsPageSize.value,
      status: jobsStatus.value,
      date_from: jobsDateFrom.value || null,
      date_to: jobsDateTo.value || null,
      search: jobsSearch.value || null,
    });
    jobs.value = res?.items || [];
    jobsTotal.value = res?.total || 0;
  } catch (e) {
    console.error("Error loading jobs:", e);
    jobs.value = [];
    jobsTotal.value = 0;
  } finally {
    jobsLoading.value = false;
  }
};

const loadGroups = async () => {
  if (groupsLoading.value) return;
  groupsLoading.value = true;
  try {
    const res = await metricsAPI.getJobQueueGroups({
      page: groupsPage.value,
      page_size: groupsPageSize.value,
      status: groupsStatus.value,
      date_from: groupsDateFrom.value || null,
      date_to: groupsDateTo.value || null,
      search: groupsSearch.value || null,
    });
    groups.value = res?.items || [];
    groupsTotal.value = res?.total || 0;
  } catch (e) {
    console.error("Error loading job groups:", e);
    groups.value = [];
    groupsTotal.value = 0;
  } finally {
    groupsLoading.value = false;
  }
};

const changeJobsPage = (page) => {
  if (page < 1 || page === jobsPage.value) return;
  jobsPage.value = page;
  loadJobs();
};

const changeGroupsPage = (page) => {
  if (page < 1 || page === groupsPage.value) return;
  groupsPage.value = page;
  loadGroups();
};

const onJobsFilterChange = () => {
  jobsPage.value = 1;
  loadJobs();
};

const onGroupsFilterChange = () => {
  groupsPage.value = 1;
  loadGroups();
};

onMounted(() => {
  loadJobs();
  loadGroups();
});
</script>

<style scoped>
.mb-4 {
  margin-bottom: 8px;
}

.flex {
  display: flex;
}

.flex-wrap {
  flex-wrap: wrap;
}

.items-center {
  align-items: center;
}

.justify-between {
  justify-content: space-between;
}

.gap-4 {
  gap: 16px;
}

.border,
.border-b,
.border-b-2 {
  border: 1px solid #e5e7eb;
}

.border-b {
  border-bottom-width: 1px;
}

.border-b-2 {
  border-bottom-width: 2px;
}

.rounded {
  border-radius: 4px;
}

.px-4 {
  padding-left: 16px;
  padding-right: 16px;
}

.py-2 {
  padding-top: 8px;
  padding-bottom: 8px;
}

.px-2 {
  padding-left: 8px;
  padding-right: 8px;
}

.py-1 {
  padding-top: 4px;
  padding-bottom: 4px;
}

.-mb-px {
  margin-bottom: -1px;
}

.ml-2 {
  margin-left: 8px;
}

.block {
  display: block;
}

.text-sm {
  font-size: 12px;
}

.text-xs {
  font-size: 11px;
}

.text-gray-600 {
  color: #4b5563;
}

.text-gray-500 {
  color: #6b7280;
}

.text-blue-600 {
  color: #2563eb;
}

.bg-gray-100 {
  background-color: #f3f4f6;
}

.border-blue-500 {
  border-color: #3b82f6;
}

.border-transparent {
  border-color: transparent;
}

.overflow-auto {
  overflow: auto;
}

.min-w-full {
  min-width: 100%;
}

.text-left {
  text-align: left;
}

.text-center {
  text-align: center;
}

.whitespace-pre-wrap {
  white-space: pre-wrap;
}

.max-w-xs {
  max-width: 20rem;
}

.mt-2 {
  margin-top: 8px;
}

.space-x-2 > * + * {
  margin-left: 8px;
}

button:disabled {
  cursor: not-allowed;
}

.disabled\:opacity-50:disabled {
  opacity: 0.5;
}

.hover\:text-blue-500:hover {
  color: #3b82f6;
}

.status-cell {
  font-weight: 600;
}

.status-pending {
  color: #b45309;
}

.status-running {
  color: #1d4ed8;
}

.status-completed {
  color: #15803d;
}

.status-failed {
  color: #b91c1c;
}

.filters-row {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: flex-end;
}

.filter-item {
  min-width: 160px;
}

.filter-item--wide {
  min-width: 220px;
}

.date-row {
  display: flex;
  align-items: center;
}

.mr-1 {
  margin-right: 4px;
}
</style>


